import type {
  AdoptSubscriptionRequest,
  AdoptSubscriptionResponse,
  CreateSubscriptionRequest,
  CreateAndConfirmSubscriptionRequest,
  ConfirmSubscriptionRequest,
  UpdateSubscriptionRequest,
  PauseSubscriptionRequest,
  ResumeSubscriptionRequest,
  CancelSubscriptionRequest,
  SubscriptionResponse,
  ConfirmSubscriptionResponse,
  PauseSubscriptionResponse,
  ResumeSubscriptionResponse,
  CancelSubscriptionResponse,
  GetSubscriptionItemsResponse,
  GetSubscriptionItemsParams,
  SubscriptionEstimateResponse,
  SubscriptionEstimateParams,
  SubscriptionListParams,
  SubscriptionPaymentLookupRequest,
  SubscriptionPaymentLookupResponse,
  SubscriptionInvoiceListParams,
  SubscriptionInvoiceListResponse,
  SubscriptionBillingProcessorResponse,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

/**
 * Subscription endpoints are profile-scoped: the backend requires an
 * `X-Profile-Id` header to resolve the shop / billing processor (`IR_04`
 * otherwise). Pass it through the per-call `options.headers`, e.g.
 * `subscriptions.list(params, { headers: { 'X-Profile-Id': profileId } })`.
 */
export class Subscriptions {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create and immediately confirm a subscription. `POST /subscriptions`
   *
   * For billing processors that require buyer approval (e.g. PayPal), the
   * response carries a `redirect_url` the customer must be sent to.
   */
  async createAndConfirm(
    params: CreateAndConfirmSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<ConfirmSubscriptionResponse> {
    return this.request('POST', '/subscriptions', { body: params, ...options });
  }

  /** Create a subscription without confirming it. `POST /subscriptions/create` */
  async create(
    params: CreateSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<SubscriptionResponse> {
    return this.request('POST', '/subscriptions/create', { body: params, ...options });
  }

  /**
   * Adopt an existing Creem subscription. `POST /subscriptions/adopt`
   *
   * Requires adoption to be enabled on the deployment and `X-Profile-Id` in
   * options.headers. Adoption charges nothing and emits no invoice or webhook.
   * Repeating the processor subscription ID returns the existing DeloPay ID.
   * Retry when handoff_confirmed is false. That flag confirms persistence,
   * not the merchant's external switch-over: stop processing Creem events in
   * your own integration when handing subsequent events to DeloPay.
   */
  async adopt(
    params: AdoptSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<AdoptSubscriptionResponse> {
    return this.request('POST', '/subscriptions/adopt', { body: params, ...options });
  }

  /** Retrieve a subscription by ID. `GET /subscriptions/{subscriptionId}` */
  async retrieve(subscriptionId: string, options?: RequestExtras): Promise<SubscriptionResponse> {
    return this.request('GET', `/subscriptions/${encodeURIComponent(subscriptionId)}`, {
      ...options,
    });
  }

  /**
   * Confirm a previously created subscription. `POST /subscriptions/{subscriptionId}/confirm`
   *
   * Like {@link createAndConfirm}, the response may carry a `redirect_url` for
   * processors that require buyer approval.
   */
  async confirm(
    subscriptionId: string,
    params: ConfirmSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<ConfirmSubscriptionResponse> {
    return this.request('POST', `/subscriptions/${encodeURIComponent(subscriptionId)}/confirm`, {
      body: params,
      ...options,
    });
  }

  /** Update a subscription's plan/price. `PUT /subscriptions/{subscriptionId}/update` */
  async update(
    subscriptionId: string,
    params: UpdateSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<SubscriptionResponse> {
    return this.request('PUT', `/subscriptions/${encodeURIComponent(subscriptionId)}/update`, {
      body: params,
      ...options,
    });
  }

  /** List subscriptions for the profile. `GET /subscriptions/list` */
  async list(
    params?: SubscriptionListParams,
    options?: RequestExtras,
  ): Promise<SubscriptionResponse[]> {
    return this.request('GET', '/subscriptions/list', {
      query: params as Record<string, string | number | undefined>,
      ...options,
    });
  }

  /** Estimate the cost of a subscription before creating it. `GET /subscriptions/estimate` */
  async getEstimate(
    params: SubscriptionEstimateParams,
    options?: RequestExtras,
  ): Promise<SubscriptionEstimateResponse> {
    return this.request('GET', '/subscriptions/estimate', {
      query: params as unknown as Record<string, string | number | undefined>,
      ...options,
    });
  }

  /** List purchasable subscription items (plans/addons). `GET /subscriptions/items` */
  async getItems(
    params: GetSubscriptionItemsParams,
    options?: RequestExtras,
  ): Promise<GetSubscriptionItemsResponse[]> {
    return this.request('GET', '/subscriptions/items', {
      query: params as unknown as Record<string, string | number | undefined>,
      ...options,
    });
  }

  /**
   * Pause a subscription. `POST /subscriptions/{subscriptionId}/pause`
   *
   * The body defaults to `{}` so the request still carries
   * `Content-Type: application/json` even when no params are passed — the
   * backend rejects the POST otherwise ("Unsupported content type").
   */
  async pause(
    subscriptionId: string,
    params?: PauseSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<PauseSubscriptionResponse> {
    return this.request('POST', `/subscriptions/${encodeURIComponent(subscriptionId)}/pause`, {
      body: params ?? {},
      ...options,
    });
  }

  /** Resume a paused subscription. `POST /subscriptions/{subscriptionId}/resume` */
  async resume(
    subscriptionId: string,
    params?: ResumeSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<ResumeSubscriptionResponse> {
    return this.request('POST', `/subscriptions/${encodeURIComponent(subscriptionId)}/resume`, {
      body: params ?? {},
      ...options,
    });
  }

  /** Cancel a subscription. `POST /subscriptions/{subscriptionId}/cancel` */
  async cancel(
    subscriptionId: string,
    params?: CancelSubscriptionRequest,
    options?: RequestExtras,
  ): Promise<CancelSubscriptionResponse> {
    return this.request('POST', `/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`, {
      body: params ?? {},
      ...options,
    });
  }

  /**
   * Resolve which of the given payments were raised by a subscription.
   * `POST /subscriptions/payments/lookup`
   *
   * The linkage exists in one direction only — an invoice points at the payment
   * it settled, and nothing is stamped on the payment — so this is the only way
   * to tell a subscription charge from a one-off one when you are holding a
   * page of payments. In particular, do not use `off_session` or the presence
   * of a mandate: an ordinary saved-card charge sets those identically.
   *
   * Ids that belong to no subscription are **absent** from `links` rather than
   * returned as an error, so match on presence:
   *
   * ```ts
   * const { links } = await subscriptions.lookupPayments(
   *   { payment_ids: page.map((p) => p.payment_id) },
   *   { headers: { 'X-Profile-Id': profileId } },
   * );
   * const bySubscription = new Map(links.map((l) => [l.payment_id, l]));
   * ```
   *
   * Profile-scoped like every other subscription route, and that matters more
   * here than elsewhere: a `payment_id` is merchant-supplied and only unique
   * within a merchant, so the shop is part of the question, not an
   * optimisation. Pass the profile that owns **the payments** — for a list
   * spanning several shops, group the ids by shop and call once per group.
   *
   * At most 200 ids per call.
   */
  async lookupPayments(
    params: SubscriptionPaymentLookupRequest,
    options?: RequestExtras,
  ): Promise<SubscriptionPaymentLookupResponse> {
    return this.request('POST', '/subscriptions/payments/lookup', { body: params, ...options });
  }

  /**
   * One subscription's billing history, newest cycle first.
   * `GET /subscriptions/{subscriptionId}/invoices`
   *
   * {@link retrieve} carries only the *latest* invoice, which is the current
   * cycle — a subscription that has renewed monthly for a year has one of those
   * and twelve of these. Use this wherever a merchant needs to see what a
   * subscription has actually billed, in particular on self-charging processors
   * (Creem, PayPal) where each renewal is charged by the processor and mirrored
   * here rather than raised as a DeloPay payment.
   *
   * Two things to render honestly, both decided rather than incidental:
   *
   * - `amount` is **gross** and `refunded_amount` sits beside it. Do not net
   *   them: the difference between the two is not a smaller charge.
   * - A `refunded_amount` of `null` is "not reported" and must not render as
   *   `0`. Likewise a processor-hosted origination records a bootstrap invoice
   *   at `0` before the buyer has paid anything, so a zero amount on such a
   *   subscription is a placeholder rather than a free cycle.
   *
   * Profile-scoped like every other subscription route.
   */
  async listInvoices(
    subscriptionId: string,
    params?: SubscriptionInvoiceListParams,
    options?: RequestExtras,
  ): Promise<SubscriptionInvoiceListResponse> {
    return this.request('GET', `/subscriptions/${encodeURIComponent(subscriptionId)}/invoices`, {
      query: params as Record<string, string | number | undefined>,
      ...options,
    });
  }

  /**
   * Which billing processor this shop's subscriptions run on.
   * `GET /subscriptions/billing_processor`
   *
   * The same mapping is derivable from the connector inventory
   * (`GET /account/{merchant_id}/connectors`), but that route is gated by a
   * connector-read permission granted independently of subscriptions — so a
   * role authorised to create subscriptions could be unable to learn which
   * processor it was creating them on. This answers under the same
   * authorization as the rest of the subscription API.
   *
   * Reach for it when the client must branch on the processor *before* calling
   * — origination differs by processor, and guessing is destructive. Resolve
   * the shop's `billing_processor_id` first: a shop that runs no subscriptions
   * has none assigned, and this route has no identity to report for it.
   */
  async getBillingProcessor(
    options?: RequestExtras,
  ): Promise<SubscriptionBillingProcessorResponse> {
    return this.request('GET', '/subscriptions/billing_processor', { ...options });
  }
}
